import { useState, useCallback } from 'react';
import { useAxios, APIError } from '../core/_axios';
import { Account } from '../core/_models';
import { uploadLogoForAccount } from '../core/_requests';
import ModalFuneralHomeLogoCropper from './cropper/ModalFuneralHomeLogoCropper';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

type Props = {
    account: Account;
    onClose?: () => void;          // parent can hide the uploader after “Cancel” or upload success
};

export default function FuneralHomeLogoUploader({ account, onClose }: Props) {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [logoBlob, setLogoBlob] = useState<Blob | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleUpload = useCallback(async () => {
        if (!logoBlob) return;
        setUploading(true);
        setError(null);

        try {
            await uploadLogoForAccount(axios, account.unique_identifier, logoBlob);
            window.location.reload();               // refresh to pull the new logo URL
        } catch (e) {
            const msg =
                e instanceof APIError ? e.message : copy.Common.messages.errorOccurred;
            setError(msg);
        } finally {
            setUploading(false);
        }
    }, [axios, account.unique_identifier, logoBlob]);

    const handleCancel = () => {
        setLogoBlob(null);
        setError(null);
        setUploading(false);
        onClose?.();                              // tell parent we’re done
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label className="block mb-2 text-sm font-medium">
                    {copy.Account.details.uploadLogo}
                </label>

                <ModalFuneralHomeLogoCropper
                    setPhotoBlob={setLogoBlob}
                    currentPhotoUrl={account.public_data.friends_and_relatives_pdf_footer_logo_url || ''}
                />
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">
                    {error}
                </div>
            )}

            <div className="flex gap-2">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                    disabled={uploading}
                >
                    {copy.Common.buttons.cancel}
                </button>

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={!logoBlob || uploading}
                >
                    {uploading ? (
                        <>
                            <span className="animate-spin inline-block mr-2">&#9696;</span>
                            {copy.Common.labels.processing}...
                        </>
                    ) : (
                        copy.Common.buttons.upload
                    )}
                </button>
            </div>
        </div>
    );
}
